import { useCallback, useState } from 'react'

/**
 * The two languages every legal page is written in. Indonesian is the default
 * because the service is sold in Indonesia and priced in rupiah; English exists
 * for the developers who integrate the API from outside it.
 */
export type LegalLanguage = 'id' | 'en'

export const LEGAL_LANGUAGES: { id: LegalLanguage; label: string }[] = [
  { id: 'id', label: 'Bahasa Indonesia' },
  { id: 'en', label: 'English' },
]

/** A paragraph or a bullet list. Enough shape for a policy, nothing more. */
export type LegalBlock = { kind: 'p'; text: string } | { kind: 'ul'; items: string[] }

export interface LegalSection {
  /** Also the anchor id, so a clause can be linked to directly. */
  id: string
  heading: string
  blocks: LegalBlock[]
}

export interface LegalDocument {
  title: string
  subtitle: string
  /** ISO date; rendered in the reader's language. */
  effectiveDate: string
  effectiveLabel: string
  summary: string
  tocLabel: string
  sections: LegalSection[]
}

const STORAGE_KEY = 'ytclip.legalLanguage'

function readStoredLanguage(): LegalLanguage {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'en' ? 'en' : 'id'
  } catch {
    return 'id'
  }
}

/**
 * Remembers which language the reader picked, so moving between the privacy
 * policy and the terms does not silently switch them back to Indonesian.
 */
export function useLegalLanguage() {
  const [language, setStored] = useState<LegalLanguage>(readStoredLanguage)

  const setLanguage = useCallback((next: LegalLanguage) => {
    setStored(next)
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // Private browsing or blocked storage; the choice still holds for this visit.
    }
  }, [])

  return { language, setLanguage }
}
